import React, { useState } from 'react';
import axios from 'axios';
import './CandidateRegistration.css';

const CandidateRegistration = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a valid CSV file');
        return;
      }
      setFile(selectedFile);
      setError('');
      setResults(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('https://sims.ethnotech.in/api/upload-candidates', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        setResults(response.data);
        setFile(null);
        // Reset file input
        document.getElementById('csv-file-input').value = '';
      } else {
        setError(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError('Upload failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = () => {
    try {
      if (!results?.results?.length) {
        setError('No data available to download');
        return;
      }

      // Extract user details from results
      const headers = ['User name', 'Candidate ID', 'Name prefix (Mr/Ms)', 'First name'];
      const userDetails = results.results.map(result => {
        // Extract CAN number from message if it's not directly available
        let canNumber = result.can_number;
        if (!canNumber && typeof result.response_message === 'string') {
          const match = result.response_message.match(/CAN[_-](\d+)/);
          if (match) {
            canNumber = match[1];
          }
        }
        
        // Format CAN number and handle cases where it might already have the prefix
        let formattedCAN = canNumber || '';
        if (formattedCAN && !formattedCAN.startsWith('CAN_')) {
          formattedCAN = `CAN_${formattedCAN}`;
        }
        
        // Return formatted user details
        return {
          'User name': formattedCAN,
          'Candidate ID': formattedCAN,
          'Name prefix (Mr/Ms)': result.namePrefix || 'Mr',
          'First name': result.firstName || ''
        };
      });

      if (userDetails.length === 0) {
        setError('No valid user details found in the response');
        return;
      }

      // Convert to CSV
      const csvContent = [
        headers.join(','), // Header row
        ...userDetails.map(user => 
          headers.map(header => `"${String(user[header] || '').replace(/"/g, '""')}"`).join(',')
        )
      ].join('\r\n');

      // Create and trigger download
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `registration_results_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to generate download. Please try again.');
    }
  };

  const getStatusIcon = (success) => {
    return success ? '✅' : '❌';
  };

  const getStatusColor = (success) => {
    return success ? '#28a745' : '#dc3545';
  };

  return (
    <div className="candidate-registration">
      <div className="registration-header">
        <h2>Candidate Registration</h2>
        <p>Upload CSV file to register multiple candidates</p>
      </div>

      <div className="upload-section">
        <div className="file-upload-area">
          <div className="upload-icon">📁</div>
          <h3>Upload CSV File</h3>
          <p>Select a CSV file containing candidate information</p>
          
          <input
            id="csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={loading}
            className="file-input"
          />
          
          {file && (
            <div className="file-info">
              <span className="file-name">📄 {file.name}</span>
              <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
            </div>
          )}
        </div>

        <div className="csv-format-info">
          <h4>Required CSV Format:</h4>
          <div className="format-fields">
            <span>namePrefix</span>
            <span>firstName</span>
            <span>gender</span>
            <span>dob (YYYY-MM-DD)</span>
            <span>fatherName</span>
            <span>guardianName</span>
            <span>email</span>
            <span>phone</span>
            <span>countryCode</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || loading}
          className={`upload-button ${loading ? 'loading' : ''}`}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              {uploadProgress > 0 ? `Uploading... ${uploadProgress}%` : 'Processing...'}
            </>
          ) : (
            'Upload & Register Candidates'
          )}
        </button>
      </div>

      {results && (
        <div className="results-section">
          <div className="results-header">
            <h3>Registration Results</h3>
            <div className="results-summary">
              <div className="summary-item">
                <span className="summary-label">Total Processed:</span>
                <span className="summary-value">{results.processed}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Successful:</span>
                <span className="summary-value success">{results.results.filter(r => r.success).length}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Failed:</span>
                <span className="summary-value error">{results.results.filter(r => !r.success).length}</span>
              </div>
            </div>
            <button onClick={handleDownload} className="download-button">
              📥 Download Results CSV
            </button>
          </div>

          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status Code</th>
                  <th>Response Message</th>
                </tr>
              </thead>
              <tbody>
                {results.results.map((result, index) => (
                  <tr key={index} className={result.success ? 'success-row' : 'error-row'}>
                    <td>
                      <span style={{ color: getStatusColor(result.success) }}>
                        {getStatusIcon(result.success)}
                      </span>
                    </td>
                    <td>{result.firstName}</td>
                    <td>{result.email}</td>
                    <td>{result.status_code}</td>
                    <td className="response-message">{result.response_message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateRegistration;
